import { useState, useEffect } from 'react';
import { getCustomers, recordPOSTransaction } from '../../api/distributionService';
import { Customer } from '../../models/distribution';
import { useAuth } from '../../store/AuthContext';
import { useParams } from 'react-router-dom';

export default function POSForm() {
  const { user } = useAuth();
  const { locationId } = useParams<{ locationId: string }>();
  
  const [formData, setFormData] = useState({
    customerId: '',
    amount: '',
    transactionType: 'Withdrawal',
    charges: '0.00',
    transactionDate: new Date().toISOString().split('T')[0],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: '', type: '' });
  const [customers, setCustomers] = useState<Customer[]>([]);

  // Calculate charges based on amount
  const calculateCharges = (amount: number): number => {
    if (amount <= 200) {
      return 20;
    } else if (amount <= 500) {
      return 50;
    } else {
      // Incremental of 100 for every 5000
      return Math.floor(amount / 5000) * 100;
    }
  };

  useEffect(() => {
    const loadCustomers = async () => {
      const data = await getCustomers(locationId ?? '');
      setCustomers(data);
    };
    loadCustomers();
  }, [locationId]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(e.target.value) || 0;
    const charges = calculateCharges(amount);
    
    setFormData(prev => ({
      ...prev,
      amount: e.target.value,
      charges: charges.toFixed(2)
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await recordPOSTransaction({
        locationId: locationId ?? '',
        customerId: formData.customerId || undefined,
        transactionType: formData.transactionType,
        amount: parseFloat(formData.amount),
        charges: parseFloat(formData.charges),
        transactionDate: formData.transactionDate,
      });
      
      setSubmitMessage({ text: 'POS transaction recorded successfully!', type: 'success' });
      setFormData({
        customerId: '',
        transactionType: 'Withdrawal',
        amount: '',
        charges: '0.00',
        transactionDate: new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      setSubmitMessage({ text: 'Failed to record transaction', type: 'error' });
      console.error('Transaction error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">POS Transactions</h1>
      
      <div className="card p-4">
        <h3 className="text-lg font-semibold mb-4">Record POS Transaction</h3>
        
        {submitMessage.text && (
          <div className={`mb-4 p-3 rounded ${submitMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {submitMessage.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Customer (Optional)</label>
            <select
              name="customerId"
              value={formData.customerId}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} ({customer.phone})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Transaction Type</label>
            <select
              name="transactionType"
              value={formData.transactionType}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            >
              <option value="Withdrawal">Withdrawal</option>
              <option value="Deposit">Deposit</option>
              <option value="Airtime">Airtime</option>
              <option value="Data">Data</option>
              <option value="BillPayment">Bill Payment</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Amount *
              {errors.amount && <span className="text-red-500 text-xs ml-2">{errors.amount}</span>}
            </label>
            <input
              type="number"
              name="amount"
              min="0.01"
              step="0.01"
              value={formData.amount}
              onChange={handleAmountChange}
              className={`w-full p-2 border rounded ${errors.amount ? 'border-red-500' : ''}`}
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Charges
            </label>
            <input
              type="number"
              name="charges"
              value={formData.charges}
              readOnly
              className="w-full p-2 border rounded bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Charges calculation: 
              {formData.amount && ` ₦${formData.charges} for ₦${formData.amount}`}
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Total Amount</label>
            <div className="p-2 bg-gray-100 rounded">
              {formData.amount 
                ? (parseFloat(formData.amount) + parseFloat(formData.charges)).toFixed(2)
                : '0.00'}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 px-4 rounded text-white ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isSubmitting ? 'Processing...' : 'Record Transaction'}
          </button>
        </form>
      </div>
    </div>
  );
}